import { Link } from 'react-router-dom'

export type PausedReason = 'addon_unpaid' | 'free_domain_limit'

/** The pause reason of a /api/v1/domains row, or null. `blocked_reason` is backend-owned (addon_unpaid wins); the
 *  boolean fallbacks only cover an older backend that sends the flags without it. */
export function pausedReasonOf(d: {
  blocked_reason?: string
  blocked_for_nonpayment?: boolean
  blocked_free_limit?: boolean
}): PausedReason | null {
  if (
    d.blocked_reason === 'addon_unpaid' ||
    d.blocked_reason === 'free_domain_limit'
  )
    return d.blocked_reason
  if (d.blocked_for_nonpayment) return 'addon_unpaid'
  if (d.blocked_free_limit) return 'free_domain_limit'
  return null
}

/** One place for the per-reason wording so the list pill, the detail page and the tests cannot drift apart. */
export const pausedCopy: Record<
  PausedReason,
  { pill: string; title: string; body: string; cta: string }
> = {
  free_domain_limit: {
    pill: 'Paused — Free plan covers one domain',
    title: 'is paused on the Free plan',
    body: 'Free watches one domain and this is not it, so no reports are collected for it and a scammer spoofing it goes unseen. Its history is kept and returns the moment you upgrade.',
    cta: 'Upgrade to restore it →',
  },
  addon_unpaid: {
    pill: 'Locked — unpaid invoice',
    title: 'is locked: the invoice for the extra domain is unpaid',
    body: 'Reports are still being collected. Pay the invoice on the billing page to unlock it.',
    cta: 'Go to billing →',
  },
}

/**
 * Replaces the domain detail view for a blocked domain. Delete stays reachable (domains list) - customers must always
 * be able to shrink their footprint. The domain's data routes answer 402 anyway, so rendering nothing else is honest.
 */
export function DomainPausedState({
  domainName,
  reason,
}: {
  domainName: string
  reason: PausedReason
}) {
  const c = pausedCopy[reason]
  return (
    <div data-testid="domain-paused-state">
      <h1 className="text-eg-2xl mb-3">
        {domainName} {c.title}
      </h1>
      <p className="mb-6">{c.body}</p>
      <p>
        <Link
          data-testid="domain-paused-upgrade"
          to="/dashboard/billing"
          className="btn btn-primary"
        >
          {c.cta}
        </Link>{' '}
        <Link data-testid="domain-paused-manage" to="/dashboard/domains">
          Back to domains (you can remove it there)
        </Link>
      </p>
    </div>
  )
}
